import React, { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import NavHeader from '@/components/NavHeader';
import { useAuth } from '@pooflabs/web';
import { toast } from 'sonner';
import { PageLayout } from '@/components/poof-ui';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import {
  subscribeAllPrizePoolDeposits,
  setPrizePoolDeposits,
} from '@/lib/collections/prizePoolDeposits';
import type { PrizePoolDepositsResponse } from '@/lib/collections/prizePoolDeposits';
import {
  subscribeAllPrizePoolRaffles,
} from '@/lib/collections/prizePoolRaffles';
import type { PrizePoolRafflesResponse } from '@/lib/collections/prizePoolRaffles';
import { Time, Address } from '@/lib/db-client';
import { BlurFade } from '@/components/effects/BlurFade';
import { Particles } from '@/components/effects/Particles';
import { ShootingStars } from '@/components/effects/ShootingStars';
import {
  getTokenByMint,
  parseTokenAmount,
  formatTokenAmount,
  SOL,
  BONK,
  SKR,
  MEW,
  USDC,
} from '@/utils/tokenConfig';
import { truncateAddress } from '@/utils/pointsCalculator';
import {
  Lock,
  Trophy,
  Crown,
  Star,
  Dice3,
  Heart,
  Loader2,
  Calendar,
  ChevronRight,
} from 'lucide-react';

// ─── Types ─────────────────────────────────────────────────────────────────────

type PoolType = 'top1' | 'top10' | 'top100';

interface PoolConfig {
  id: PoolType;
  name: string;
  tagline: string;
  description: string;
  Icon: React.FC<{ size?: number; style?: React.CSSProperties }>;
  rankLabel: string;
  // visual theme
  primaryColor: string;
  glowColor: string;
  borderColor: string;
  bgGradient: string;
  headerGlow: string;
  badgeBg: string;
  badgeBorder: string;
}

const POOLS: PoolConfig[] = [
  {
    id: 'top1',
    name: 'The Crown',
    tagline: 'Awarded to the #1 Most Selfless User',
    description: 'Not a raffle — the full pool goes directly to whoever holds the #1 spot on the Selfless Leaderboard at month end. The ultimate prize for the most giving soul.',
    Icon: Crown,
    rankLabel: 'Top 1',
    primaryColor: 'hsl(43 75% 68%)',
    glowColor: 'hsl(38 65% 57% / 0.5)',
    borderColor: 'hsl(38 65% 57% / 0.55)',
    bgGradient: 'linear-gradient(145deg, hsl(30 8% 7%) 0%, hsl(38 35% 9%) 50%, hsl(38 25% 11%) 100%)',
    headerGlow: 'radial-gradient(ellipse 80% 50% at 50% 0%, hsl(38 65% 57% / 0.18), transparent)',
    badgeBg: 'hsl(38 65% 57% / 0.15)',
    badgeBorder: 'hsl(38 65% 57% / 0.4)',
  },
  {
    id: 'top10',
    name: 'The Elite',
    tagline: 'Raffled among the Top 10 Selfless Users',
    description: 'One lucky winner is drawn from the top 10 leaderboard spots each month. The selfless elite compete for this distinguished prize.',
    Icon: Star,
    rankLabel: 'Top 10',
    primaryColor: 'hsl(210 25% 78%)',
    glowColor: 'hsl(210 20% 72% / 0.35)',
    borderColor: 'hsl(210 20% 40% / 0.55)',
    bgGradient: 'linear-gradient(145deg, hsl(30 8% 7%) 0%, hsl(210 12% 9%) 50%, hsl(210 8% 11%) 100%)',
    headerGlow: 'radial-gradient(ellipse 80% 50% at 50% 0%, hsl(210 20% 72% / 0.12), transparent)',
    badgeBg: 'hsl(210 20% 72% / 0.12)',
    badgeBorder: 'hsl(210 20% 50% / 0.35)',
  },
  {
    id: 'top100',
    name: "The People's Pool",
    tagline: 'Raffled among the Top 100 Selfless Users',
    description: 'The widest net — any of the top 100 leaderboard users can win this monthly raffle. More chances, more excitement, more community.',
    Icon: Dice3,
    rankLabel: 'Top 100',
    primaryColor: 'hsl(28 65% 58%)',
    glowColor: 'hsl(28 55% 48% / 0.35)',
    borderColor: 'hsl(28 45% 35% / 0.55)',
    bgGradient: 'linear-gradient(145deg, hsl(30 8% 7%) 0%, hsl(28 12% 9%) 50%, hsl(28 8% 11%) 100%)',
    headerGlow: 'radial-gradient(ellipse 80% 50% at 50% 0%, hsl(28 55% 48% / 0.12), transparent)',
    badgeBg: 'hsl(28 55% 48% / 0.12)',
    badgeBorder: 'hsl(28 45% 35% / 0.35)',
  },
];

const TOKENS = [SOL, BONK, SKR, MEW, USDC];

// ─── Helper: Current month start (Unix seconds) ─────────────────────────────

function getMonthStart(): number {
  const now = new Date();
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1, 0, 0, 0));
  return Math.floor(start.getTime() / 1000);
}

function getNextMonthFirst(): Date {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
}

function formatNextRaffle(): string {
  const next = getNextMonthFirst();
  return next.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

function getPoolTotals(
  deposits: PrizePoolDepositsResponse[],
  poolType: PoolType,
  monthStart: number,
): Record<string, number> {
  const totals: Record<string, number> = {};
  for (const d of deposits) {
    if (d.poolType === poolType && d.ts >= monthStart) {
      totals[d.tokenMint] = (totals[d.tokenMint] ?? 0) + d.amount;
    }
  }
  return totals;
}

// ─── Token Pill ─────────────────────────────────────────────────────────────

const TokenPill: React.FC<{
  mint: string;
  selected: boolean;
  onClick: () => void;
  accentColor: string;
  accentBg: string;
  accentBorder: string;
}> = ({ mint, selected, onClick, accentColor, accentBg, accentBorder }) => {
  const t = getTokenByMint(mint);
  return (
    <button
      onClick={onClick}
      className="px-4 py-2 rounded-full text-sm font-bold tracking-wide transition-all duration-200"
      style={{
        background: selected ? t.brandColorBg : 'hsl(30 6% 7%)',
        color: selected ? t.brandColor : 'hsl(35 8% 45%)',
        border: selected ? `1px solid ${t.brandColorBorder}` : '1px solid hsl(35 12% 14%)',
        boxShadow: selected ? `0 0 12px ${t.brandColorBg}` : 'none',
        transform: selected ? 'scale(1.05)' : 'scale(1)',
      }}
    >
      {t.symbol}
    </button>
  );
};

// ─── Pool Totals Display ─────────────────────────────────────────────────────

const PoolTotals: React.FC<{
  totals: Record<string, number>;
  primaryColor: string;
}> = ({ totals, primaryColor }) => {
  const activeMints = TOKENS.filter((m) => (totals[m] ?? 0) > 0);

  if (activeMints.length === 0) {
    return (
      <p className="text-sm" style={{ color: 'hsl(35 8% 35%)' }}>
        No contributions yet this month — be the first!
      </p>
    );
  }

  return (
    <div className="flex flex-wrap gap-4">
      {activeMints.map((mint) => {
        const t = getTokenByMint(mint);
        return (
          <div key={mint} className="text-center">
            <div
              className="text-2xl font-bold leading-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: primaryColor }}
            >
              {formatTokenAmount(totals[mint] ?? 0, mint)}
            </div>
            <div className="text-xs mt-0.5" style={{ color: 'hsl(35 8% 40%)' }}>
              {t.symbol} in pool
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── Donate Modal ────────────────────────────────────────────────────────────

const DonateModal: React.FC<{
  open: boolean;
  pool: PoolConfig | null;
  walletAddress: string;
  onClose: () => void;
}> = ({ open, pool, walletAddress, onClose }) => {
  const [selectedToken, setSelectedToken] = useState(SOL);
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleContribute = useCallback(async () => {
    if (!pool || !walletAddress) return;
    const amountNum = parseFloat(amount);
    if (!amountNum || amountNum <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    const rawAmount = parseTokenAmount(amountNum, selectedToken);
    if (rawAmount <= 0) {
      toast.error('Amount too small');
      return;
    }

    setSubmitting(true);
    const depositId = `${walletAddress}_${pool.id}_${Date.now()}`;
    const success = await setPrizePoolDeposits(depositId, {
      donor: Address.publicKey(walletAddress),
      poolType: pool.id,
      tokenMint: selectedToken,
      amount: rawAmount,
      ts: Time.Now,
    });

    setSubmitting(false);
    if (success) {
      toast.success(
        `Contributed ${formatTokenAmount(rawAmount, selectedToken)} to ${pool.name}!`
      );
      setAmount('');
      onClose();
    } else {
      toast.error('Contribution failed — please try again');
    }
  }, [pool, walletAddress, amount, selectedToken, onClose]);

  if (!pool) return null;

  const { primaryColor, bgGradient, borderColor } = pool;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        style={{
          background: bgGradient,
          border: `1px solid ${borderColor}`,
          color: 'hsl(40 15% 92%)',
          maxWidth: '440px',
        }}
      >
        <DialogHeader>
          <DialogTitle
            className="flex items-center gap-2"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: primaryColor }}
          >
            <pool.Icon size={18} style={{ color: primaryColor }} />
            Contribute to {pool.name}
          </DialogTitle>
          <DialogDescription style={{ color: 'hsl(35 8% 50%)' }}>
            {pool.tagline}. All contributions this month accumulate until the raffle on{' '}
            <span style={{ color: primaryColor }}>{formatNextRaffle()}</span>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          {/* Token selector */}
          <div>
            <label className="text-xs font-medium uppercase tracking-wider mb-2 block" style={{ color: 'hsl(35 8% 45%)' }}>
              Token
            </label>
            <div className="flex gap-2">
              {TOKENS.map((mint) => (
                <TokenPill
                  key={mint}
                  mint={mint}
                  selected={selectedToken === mint}
                  onClick={() => setSelectedToken(mint)}
                  accentColor={primaryColor}
                  accentBg={pool.badgeBg}
                  accentBorder={pool.badgeBorder}
                />
              ))}
            </div>
          </div>

          {/* Amount input */}
          <div>
            <label className="text-xs font-medium uppercase tracking-wider mb-2 block" style={{ color: 'hsl(35 8% 45%)' }}>
              Amount ({getTokenByMint(selectedToken).symbol})
            </label>
            <Input
              type="number"
              min="0"
              step="any"
              placeholder={(selectedToken === BONK || selectedToken === MEW) ? 'e.g. 10000' : (selectedToken === USDC) ? 'e.g. 10' : 'e.g. 1'}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              style={{
                background: 'hsl(30 5% 10%)',
                border: '1px solid hsl(35 12% 18%)',
                color: 'hsl(40 15% 88%)',
              }}
            />
          </div>

          <Button
            onClick={handleContribute}
            disabled={submitting || !amount || parseFloat(amount) <= 0}
            className="w-full py-5 font-medium"
            style={{
              background: `linear-gradient(135deg, ${primaryColor}BB, ${primaryColor})`,
              color: 'hsl(30 8% 4%)',
              border: 'none',
              opacity: submitting || !amount || parseFloat(amount) <= 0 ? 0.5 : 1,
            }}
          >
            {submitting ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" />
                Contributing…
              </>
            ) : (
              <>
                <Trophy size={16} className="mr-2" />
                Contribute to Pool
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── Pool Card ───────────────────────────────────────────────────────────────

const PoolCard: React.FC<{
  pool: PoolConfig;
  totals: Record<string, number>;
  onDonate: () => void;
  delay: number;
  isConnected: boolean;
}> = ({ pool, totals, onDonate, delay, isConnected }) => {
  const {
    name, tagline, description, Icon, rankLabel,
    primaryColor, glowColor, borderColor, bgGradient,
    headerGlow, badgeBg, badgeBorder,
  } = pool;

  return (
    <BlurFade delay={delay}>
      <div
        className="relative rounded-2xl overflow-hidden flex flex-col h-full group"
        style={{
          background: bgGradient,
          border: `1px solid ${borderColor}`,
          boxShadow: `0 0 40px ${glowColor}`,
        }}
      >
        {/* Header glow */}
        <div className="absolute inset-x-0 top-0 h-40 pointer-events-none" style={{ background: headerGlow }} />

        {/* Animated corner decoration */}
        <div
          className="absolute top-0 right-0 w-32 h-32 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
          style={{ background: `radial-gradient(circle at top right, ${glowColor}, transparent)` }}
        />

        <div className="relative z-10 p-6 flex flex-col flex-1">
          {/* Pool rank badge */}
          <div className="flex items-start justify-between mb-4">
            <span
              className="text-xs font-bold tracking-widest uppercase px-2.5 py-1 rounded-full"
              style={{ background: badgeBg, color: primaryColor, border: `1px solid ${badgeBorder}` }}
            >
              {rankLabel}
            </span>
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: badgeBg, border: `1px solid ${badgeBorder}` }}
            >
              <Icon size={18} style={{ color: primaryColor }} />
            </div>
          </div>

          {/* Pool name */}
          <h2
            className="text-2xl font-semibold mb-1 leading-tight"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: primaryColor }}
          >
            {name}
          </h2>
          <p className="text-sm mb-3 font-medium" style={{ color: 'hsl(40 15% 72%)' }}>
            {tagline}
          </p>
          <p className="text-xs leading-relaxed mb-6" style={{ color: 'hsl(35 8% 48%)' }}>
            {description}
          </p>

          {/* This month's pool */}
          <div
            className="rounded-xl p-4 mb-5"
            style={{ background: 'hsl(30 6% 5% / 0.7)', border: `1px solid ${borderColor}` }}
          >
            <div className="text-xs font-medium uppercase tracking-wider mb-3" style={{ color: 'hsl(35 8% 40%)' }}>
              This Month's Pool
            </div>
            <PoolTotals totals={totals} primaryColor={primaryColor} />
          </div>

          {/* Donate button */}
          <div className="mt-auto">
            <Button
              onClick={onDonate}
              className="w-full py-4 font-medium tracking-wide"
              style={{
                background: badgeBg,
                color: primaryColor,
                border: `1px solid ${badgeBorder}`,
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = `${primaryColor}22`;
                (e.currentTarget as HTMLButtonElement).style.boxShadow = `0 0 20px ${glowColor}`;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = badgeBg;
                (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none';
              }}
            >
              <Trophy size={14} className="mr-2" />
              {isConnected ? 'Donate to Pool' : 'Connect to Donate'}
            </Button>
          </div>
        </div>
      </div>
    </BlurFade>
  );
};

// ─── Raffle History ──────────────────────────────────────────────────────────

const RaffleHistory: React.FC<{ raffles: PrizePoolRafflesResponse[] }> = ({ raffles }) => {
  const sorted = [...raffles].sort((a, b) => b.ts - a.ts);

  const poolNameMap: Record<PoolType, string> = {
    top1: 'The Crown',
    top10: 'The Elite',
    top100: "The People's Pool",
  };

  const poolIconMap: Record<PoolType, React.FC<{ size?: number; style?: React.CSSProperties }>> = {
    top1: Crown,
    top10: Star,
    top100: Dice3,
  };

  const poolColorMap: Record<PoolType, string> = {
    top1: 'hsl(43 75% 68%)',
    top10: 'hsl(210 25% 78%)',
    top100: 'hsl(28 65% 58%)',
  };

  if (sorted.length === 0) {
    return (
      <BlurFade delay={0.15}>
        <div
          className="rounded-xl p-10 text-center"
          style={{ background: 'hsl(30 6% 7%)', border: '1px solid hsl(35 12% 14%)' }}
        >
          <Calendar size={32} className="mx-auto mb-3 opacity-30" style={{ color: 'hsl(38 65% 57%)' }} />
          <p className="font-semibold mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", color: 'hsl(40 15% 70%)' }}>
            No Raffles Yet
          </p>
          <p className="text-sm" style={{ color: 'hsl(35 8% 40%)' }}>
            The first raffle draws on <span style={{ color: 'hsl(43 75% 68%)' }}>{formatNextRaffle()}</span>. Contribute now to be part of history.
          </p>
        </div>
      </BlurFade>
    );
  }

  return (
    <BlurFade delay={0.15}>
      <div
        className="rounded-xl overflow-hidden"
        style={{ background: 'hsl(30 6% 7%)', border: '1px solid hsl(35 12% 14%)' }}
      >
        {/* Column headers */}
        <div
          className="grid px-5 py-3 text-xs font-medium uppercase tracking-wider"
          style={{
            gridTemplateColumns: '1fr 1fr 1fr 1fr auto',
            color: 'hsl(35 8% 38%)',
            gap: '1rem',
            borderBottom: '1px solid hsl(35 12% 12%)',
          }}
        >
          <span>Month</span>
          <span>Pool</span>
          <span>Winner</span>
          <span>Prize</span>
          <span className="text-right">Date</span>
        </div>

        <div>
          {sorted.map((raffle) => {
            const pool = raffle.poolType as PoolType;
            const color = poolColorMap[pool] ?? 'hsl(38 65% 57%)';
            const PoolIcon = poolIconMap[pool] ?? Trophy;
            const poolName = poolNameMap[pool] ?? raffle.poolType;
            const date = new Date(raffle.ts * 1000).toLocaleDateString('en-US', {
              month: 'short', day: 'numeric',
            });
            return (
              <div
                key={raffle.id}
                className="grid items-center px-5 py-3.5 transition-colors"
                style={{
                  gridTemplateColumns: '1fr 1fr 1fr 1fr auto',
                  gap: '1rem',
                  borderBottom: '1px solid hsl(35 12% 10%)',
                }}
              >
                <span
                  className="text-sm font-medium"
                  style={{ color: 'hsl(40 15% 72%)' }}
                >
                  {raffle.month}
                </span>
                <div className="flex items-center gap-1.5">
                  <PoolIcon size={12} style={{ color }} />
                  <span className="text-sm font-medium" style={{ color }}>
                    {poolName}
                  </span>
                </div>
                <span className="font-mono text-sm" style={{ color: 'hsl(40 15% 70%)' }}>
                  {truncateAddress(raffle.winner)}
                </span>
                <span className="text-sm font-bold" style={{ color }}>
                  {formatTokenAmount(raffle.amount, raffle.tokenMint)}
                </span>
                <span className="text-xs text-right" style={{ color: 'hsl(35 8% 40%)' }}>
                  {date}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </BlurFade>
  );
};

// ─── Your Contributions ──────────────────────────────────────────────────────

const YourContributions: React.FC<{
  deposits: PrizePoolDepositsResponse[];
  walletAddress: string;
}> = ({ deposits, walletAddress }) => {
  const monthStart = useMemo(() => getMonthStart(), []);

  const myDeposits = useMemo(() => {
    return deposits.filter((d) => d.donor === walletAddress && d.ts >= monthStart);
  }, [deposits, walletAddress, monthStart]);

  const byPool = useMemo(() => {
    const map: Record<string, Record<string, number>> = {
      top1: {}, top10: {}, top100: {},
    };
    for (const d of myDeposits) {
      if (map[d.poolType]) {
        map[d.poolType][d.tokenMint] = (map[d.poolType][d.tokenMint] ?? 0) + d.amount;
      }
    }
    return map;
  }, [myDeposits]);

  const poolNames: Record<string, string> = {
    top1: 'The Crown', top10: 'The Elite', top100: "The People's Pool",
  };
  const poolColors: Record<string, string> = {
    top1: 'hsl(43 75% 68%)', top10: 'hsl(210 25% 78%)', top100: 'hsl(28 65% 58%)',
  };
  const poolIcons: Record<string, React.FC<{ size?: number; style?: React.CSSProperties }>> = {
    top1: Crown, top10: Star, top100: Dice3,
  };

  if (myDeposits.length === 0) {
    return (
      <div
        className="rounded-xl p-8 text-center"
        style={{ background: 'hsl(30 6% 7%)', border: '1px solid hsl(35 12% 14%)' }}
      >
        <Heart size={24} className="mx-auto mb-3" style={{ color: 'hsl(35 8% 30%)' }} />
        <p className="text-sm" style={{ color: 'hsl(35 8% 42%)' }}>
          You haven't contributed to any prize pools this month yet.
        </p>
      </div>
    );
  }

  return (
    <div className="grid sm:grid-cols-3 gap-4">
      {(['top1', 'top10', 'top100'] as PoolType[]).map((poolId) => {
        const poolTotals = byPool[poolId];
        const activeMints = TOKENS.filter((m) => (poolTotals[m] ?? 0) > 0);
        if (activeMints.length === 0) return null;
        const color = poolColors[poolId];
        const PoolIcon = poolIcons[poolId];
        return (
          <div
            key={poolId}
            className="rounded-xl p-4"
            style={{ background: 'hsl(30 6% 7%)', border: `1px solid ${color}33` }}
          >
            <div className="flex items-center gap-2 mb-3">
              <PoolIcon size={13} style={{ color }} />
              <span className="text-sm font-semibold" style={{ color, fontFamily: "'Cormorant Garamond', serif" }}>
                {poolNames[poolId]}
              </span>
            </div>
            <div className="space-y-1.5">
              {activeMints.map((mint) => (
                <div key={mint} className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: 'hsl(35 8% 48%)' }}>
                    {getTokenByMint(mint).symbol}
                  </span>
                  <span className="text-sm font-bold" style={{ color }}>
                    {formatTokenAmount(poolTotals[mint] ?? 0, mint)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── Dashboard Summary Card (exported for use in DashboardPage) ──────────────

export const PrizePoolSummaryCard: React.FC<{
  deposits: PrizePoolDepositsResponse[];
  walletAddress?: string;
}> = ({ deposits, walletAddress }) => {
  const monthStart = useMemo(() => getMonthStart(), []);

  const thisMonthDeposits = deposits.filter((d) => d.ts >= monthStart);

  // Per-pool totals (combined across tokens in SOL-equivalent for display)
  const totalsByPool = useMemo(() => {
    const map: Record<string, number> = { top1: 0, top10: 0, top100: 0 };
    for (const d of thisMonthDeposits) {
      if (map[d.poolType] !== undefined) map[d.poolType] += 1;
    }
    return map;
  }, [thisMonthDeposits]);

  const myContributions = walletAddress
    ? thisMonthDeposits.filter((d) => d.donor === walletAddress).length
    : 0;

  const poolItems = [
    { id: 'top1', name: 'Crown', Icon: Crown, color: 'hsl(43 75% 68%)', count: totalsByPool.top1 },
    { id: 'top10', name: 'Elite', Icon: Star, color: 'hsl(210 25% 78%)', count: totalsByPool.top10 },
    { id: 'top100', name: "People's", Icon: Dice3, color: 'hsl(28 65% 58%)', count: totalsByPool.top100 },
  ];

  return (
    <div className="relative overflow-hidden" style={{
      background: 'linear-gradient(135deg, hsl(30 8% 7%) 0%, hsl(43 20% 9%) 100%)',
      border: '1px solid hsl(43 40% 30% / 0.4)',
      borderRadius: '0.75rem',
      padding: '1.25rem',
    }}>
      <div className="absolute top-0 right-0 w-40 h-20 opacity-20 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at top right, hsl(43 75% 68% / 0.4), transparent)' }} />

      <div className="relative z-10 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: 'hsl(43 75% 68% / 0.15)', border: '1px solid hsl(43 75% 68% / 0.3)' }}>
            <Trophy size={16} style={{ color: 'hsl(43 75% 68%)' }} />
          </div>
          <div>
            <span className="text-xs font-medium tracking-wide uppercase" style={{ color: 'hsl(40 20% 45%)' }}>
              Prize Pools
            </span>
            <div className="text-xl font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif", color: 'hsl(43 75% 78%)' }}>
              Next draw: {formatNextRaffle()}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4 sm:ml-auto flex-wrap">
          {poolItems.map(({ id, name, Icon, color, count }) => (
            <div key={id} className="flex items-center gap-1.5">
              <Icon size={11} style={{ color }} />
              <div>
                <div className="text-xs" style={{ color: 'hsl(35 8% 40%)' }}>{name}</div>
                <div className="text-sm font-bold" style={{ color }}>{count} deposits</div>
              </div>
            </div>
          ))}
          {myContributions > 0 && (
            <div className="text-center">
              <div className="text-xs" style={{ color: 'hsl(35 8% 40%)' }}>Your Contribs</div>
              <div className="text-sm font-bold" style={{ color: 'hsl(43 75% 68%)' }}>{myContributions}</div>
            </div>
          )}
          <span className="text-xs" style={{ color: 'hsl(35 8% 38%)' }}>Explore Pools →</span>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

export const PrizePoolsPage: React.FC = () => {
  const { user, login } = useAuth();
  const walletAddress = user?.address;

  const [donatePool, setDonatePool] = useState<PoolConfig | null>(null);

  const { data: allDeposits } = useRealtimeData<PrizePoolDepositsResponse[]>(
    subscribeAllPrizePoolDeposits,
    true,
  );

  const { data: allRaffles } = useRealtimeData<PrizePoolRafflesResponse[]>(
    subscribeAllPrizePoolRaffles,
    true,
  );

  const deposits = allDeposits ?? [];
  const raffles = allRaffles ?? [];

  const monthStart = useMemo(() => getMonthStart(), []);

  const poolTotals = useMemo(() => ({
    top1: getPoolTotals(deposits, 'top1', monthStart),
    top10: getPoolTotals(deposits, 'top10', monthStart),
    top100: getPoolTotals(deposits, 'top100', monthStart),
  }), [deposits, monthStart]);

  const handleDonateClick = (pool: PoolConfig) => {
    if (!user) { login(); return; }
    setDonatePool(pool);
  };

  return (
    <PageLayout fullBleed footer={false}>
      {/* Nav */}
      <NavHeader />

      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        <ShootingStars />
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 70% 60% at 50% 0%, hsl(43 65% 57% / 0.12), transparent)' }} />
        <Particles quantity={20} color="hsl(43 75% 68%)" />
        <div className="relative z-10 container text-center">
          <BlurFade delay={0.05}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-medium mb-5 tracking-widest uppercase" style={{ background: 'hsl(43 75% 68% / 0.1)', border: '1px solid hsl(43 75% 68% / 0.25)', color: 'hsl(43 75% 68%)' }}>
              <Trophy size={10} />
              Monthly Prize Pools
            </div>
          </BlurFade>
          <BlurFade delay={0.12}>
            <h1 className="mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(2.8rem, 6vw, 5rem)', fontWeight: 600, background: 'linear-gradient(135deg, hsl(40 15% 92%) 0%, hsl(43 75% 68%) 50%, hsl(38 65% 57%) 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', lineHeight: 1.05 }}>
              Prize Pools
            </h1>
          </BlurFade>
          <BlurFade delay={0.2}>
            <p className="text-base max-w-lg mx-auto leading-relaxed" style={{ color: 'hsl(35 8% 52%)' }}>
              Contribute to monthly prize pools. Winners are drawn from the most selfless users — the generosity you give today could reward the generosity of others tomorrow.
            </p>
          </BlurFade>

          {/* Next raffle countdown */}
          <BlurFade delay={0.28}>
            <div className="inline-flex items-center gap-3 px-6 py-3 rounded-xl mt-6" style={{ background: 'hsl(30 6% 7% / 0.8)', border: '1px solid hsl(35 12% 14%)', backdropFilter: 'blur(8px)' }}>
              <Calendar size={14} style={{ color: 'hsl(38 65% 57%)' }} />
              <span className="text-sm" style={{ color: 'hsl(35 8% 52%)' }}>Next raffle draws</span>
              <span className="text-sm font-semibold" style={{ color: 'hsl(43 75% 68%)', fontFamily: "'Cormorant Garamond', serif" }}>
                {formatNextRaffle()}
              </span>
            </div>
          </BlurFade>
        </div>
      </section>

      {/* Pool Cards */}
      <div className="container pb-8">
        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto mb-16">
          {POOLS.map((pool, i) => (
            <PoolCard
              key={pool.id}
              pool={pool}
              totals={poolTotals[pool.id]}
              onDonate={() => handleDonateClick(pool)}
              delay={0.05 + i * 0.08}
              isConnected={!!user}
            />
          ))}
        </div>

        {/* Raffle History */}
        <div className="max-w-6xl mx-auto mb-16">
          <BlurFade delay={0.1}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'hsl(38 65% 57% / 0.12)', border: '1px solid hsl(38 65% 57% / 0.25)' }}>
                <Calendar size={14} style={{ color: 'hsl(38 65% 57%)' }} />
              </div>
              <h2 className="text-2xl font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif", color: 'hsl(40 15% 88%)' }}>
                Past Raffles
              </h2>
            </div>
          </BlurFade>
          <RaffleHistory raffles={raffles} />
        </div>

        {/* Your Contributions */}
        {walletAddress && (
          <div className="max-w-6xl mx-auto mb-16">
            <BlurFade delay={0.1}>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'hsl(38 65% 57% / 0.12)', border: '1px solid hsl(38 65% 57% / 0.25)' }}>
                  <Heart size={14} style={{ color: 'hsl(38 65% 57%)' }} />
                </div>
                <h2 className="text-2xl font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif", color: 'hsl(40 15% 88%)' }}>
                  Your Contributions This Month
                </h2>
              </div>
            </BlurFade>
            <BlurFade delay={0.15}>
              <YourContributions deposits={deposits} walletAddress={walletAddress} />
            </BlurFade>
          </div>
        )}

        {/* Back link */}
        <BlurFade delay={0.3}>
          <div className="max-w-6xl mx-auto text-center mt-6">
            <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm" style={{ color: 'hsl(35 8% 45%)' }}>
              <ChevronRight size={14} className="rotate-180" />
              Back to Dashboard
            </Link>
          </div>
        </BlurFade>
      </div>

      {/* Footer */}
      <footer className="py-6 px-8 text-center" style={{ borderTop: '1px solid hsl(35 12% 14%)', color: 'hsl(35 8% 36%)' }}>
        <div className="flex items-center justify-center gap-2">
          <Lock size={11} style={{ color: 'hsl(38 65% 57%)' }} />
          <span className="text-xs" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            SelfWorth · Prize pools accumulate monthly and are distributed by the protocol
          </span>
        </div>
      </footer>

      {/* Donate Modal */}
      <DonateModal
        open={!!donatePool}
        pool={donatePool}
        walletAddress={walletAddress ?? ''}
        onClose={() => setDonatePool(null)}
      />
    </PageLayout>
  );
};

export default PrizePoolsPage;
